<script setup lang="ts">
  defineOptions({ name: 'EventConnectionNotificationActions' })

  import { type PropType } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { createElementId } from '@common/core-layer/utils'
  import CPButton from '@ui/components/CPButton.vue'
  import CPText from '@ui/components/CPText.vue'
  import type { INotification } from '@common/notification-layer/types/notification.types'

  import { useEventConnectionNotification } from '../../composables/useEventConnectionNotification'

  const props = defineProps({
    notification: {
      type: Object as PropType<INotification>,
      required: true,
    },
    notifications: {
      type: Array as PropType<INotification[]>,
      required: true,
    },
  })

  const { t } = useI18n()
  const { state, connection, isChecking, isUnavailable, isResolving, accept, decline, openMessage } = useEventConnectionNotification(
    () => props.notification,
    () => props.notifications
  )
</script>

<template>
  <div class="mt-3 flex flex-wrap items-center gap-2" @click.stop @keyup.stop>
    <template v-if="state === 'incoming_pending'">
      <CPButton
        :id="createElementId('event-connection-notification', 'accept', notification._id)"
        type="primary"
        color="primary"
        size="xs"
        :disabled="isResolving"
        @click="accept"
      >
        {{ t('clientportal.events.connections.accept') }}
      </CPButton>
      <CPButton
        :id="createElementId('event-connection-notification', 'decline', notification._id)"
        type="secondary"
        color="secondary"
        size="xs"
        :disabled="isResolving"
        @click="decline"
      >
        {{ t('clientportal.events.connections.decline') }}
      </CPButton>
    </template>

    <template v-else-if="state === 'accepted'">
      <CPText size="sm" class="text-muted">
        {{ t('clientportal.events.connections.notificationConnected') }}
      </CPText>
      <CPButton
        v-if="connection?.conversationId"
        :id="createElementId('event-connection-notification', 'message', notification._id)"
        type="secondary"
        color="secondary"
        size="xs"
        @click="openMessage"
      >
        {{ t('clientportal.events.connections.message') }}
      </CPButton>
    </template>

    <CPText v-else-if="state === 'declined'" size="sm" class="text-muted">
      {{ t('clientportal.events.connections.notificationDeclined') }}
    </CPText>
    <CPText v-else-if="isChecking" size="sm" class="text-muted">
      {{ t('clientportal.events.connections.notificationChecking') }}
    </CPText>
    <CPText v-else-if="isUnavailable" size="sm" class="text-muted">
      {{ t('clientportal.events.connections.notificationUnavailable') }}
    </CPText>
  </div>
</template>
